import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class Intersection extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.intersection',
        group: 'Joins',
        title: 'ST Intersection',
        description: `
      This component creates a new table containing the intersection between the geographies
      of the input table and the geographies in another table.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'secondary',
          type: WorkflowComponentParamType.InputTable,
          title: 'Polygons table'
        },
        {
          name: 'geosource',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in source table',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'geosecondary',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in polygons table',
          parent: 'secondary',

          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    const intersectionColName = `intersection_${preparedParams.geosource}_${preparedParams.geosecondary}`
    let select = ''
    if (connection.type === Provider.DatabricksRest) {
      const leftOutputTableName = connection.quoteName(outputTablename + '_left')
      const rightOutputTableName = connection.quoteName(outputTablename + '_right')

      select = `WITH _main AS (
        SELECT /*+ REPARTITION(40, ${preparedParams.geosource}) */
        *, ${connection.processInputGeogColumn(preparedParams.geosource as string)} AS ${
        preparedParams.geosource
      }_processed FROM ${leftOutputTableName}
      ),
      _joined AS (
        SELECT /*+ REPARTITION(40, ${preparedParams.geosecondary}) */
        *, ${connection.processInputGeogColumn(preparedParams.geosecondary as string)} AS ${
        preparedParams.geosecondary
      }_processed FROM ${rightOutputTableName}
      )
      SELECT /*+ REPARTITION(40, ${intersectionColName}) */
          ${connection.processOutputGeogColumn(`ST_INTERSECTION(
            _joined.${params.geosecondary}_processed,
            _main.${params.geosource}_processed
          )`)}
        AS ${intersectionColName},
        ''' + select_clause_str + '''
      FROM
        _main,
        _joined
      WHERE
          (_main.__carto_bounds_xmax >= _joined.__carto_bounds_xmin
          AND _main.__carto_bounds_xmin <= _joined.__carto_bounds_xmax
          AND _main.__carto_bounds_ymax >= _joined.__carto_bounds_ymin
          AND _main.__carto_bounds_ymin <= _joined.__carto_bounds_ymax)
        AND
          ST_INTERSECTS(
            _main.${params.geosource}_processed,
            _joined.${params.geosecondary}_processed
          )`
    } else {
      select = `SELECT
                      ${
                        connection.type === Provider.Oracle
                          ? `SDO_GEOM.SDO_INTERSECTION(b.${preparedParams.geosecondary}, a.${preparedParams.geosource}, 0.005)`
                          : `ST_INTERSECTION(b.${preparedParams.geosecondary}, a.${preparedParams.geosource})`
                      }
                    AS ${intersectionColName},
                      a.*, %s
                    FROM ${connection.quoteName(preparedParams.source as string)} AS a
                    JOIN ${connection.quoteName(preparedParams.secondary as string)} AS b
                    ON ${
                      connection.type === Provider.Oracle
                        ? `SDO_RELATE(b.${preparedParams.geosecondary}, a.${preparedParams.geosource}, 'mask=ANYINTERACT') = 'TRUE'`
                        : `ST_INTERSECTS(b.${preparedParams.geosecondary}, a.${preparedParams.geosource})`
                    }`
    }
    const createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
    let query = ''
    const storedProc = codeType === WorkflowCodeType.StoredProcedure
    switch (connection.type) {
      case Provider.BigQuery: {
        const tokens = (preparedParams.secondary as string)?.split('.')
        const datasetname = storedProc ? connection.tempLocation : `${tokens[0]}.${tokens[1]}`
        const tablename = storedProc ? `${tempTableName(componentName, preparedParams.secondary)}` : tokens[2]
        const storedProcTable = connection.quoteName(`${connection.tempLocation}.${tablename}`)
        query = dedent`DECLARE alias STRING;
              ${
                storedProc
                  ? dedent`
                      CREATE TABLE ${storedProcTable} AS
                      SELECT * FROM ${connection.quoteName(preparedParams.secondary as string)}
                      WHERE 1=0;`
                  : ''
              }
              EXECUTE IMMEDIATE
              '''
                with __alias AS(
                  SELECT CONCAT(
                    'b.', column_name, ' AS ', column_name, '_secondary'
                  ) col_alias
                  FROM \`${datasetname}\`.INFORMATION_SCHEMA.COLUMNS
                WHERE table_name = '${tablename}'
                )
                SELECT STRING_AGG(col_alias, ', ')
                FROM __alias
              '''
              INTO alias;
              ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
              ${dedent(
                connection.executeDynamicSQL({
                  sql: createTable,
                  addQuotes: true,
                  replacements: [["'%s'", 'alias']]
                })
              )}`
        break
      }
      case Provider.Postgres: {
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempLocation.split('.')
          tablename = `${tempTableName(componentName, preparedParams.secondary)}`
          storedProcTable = connection.quoteName(`${connection.tempLocation}.${tablename}`)
        } else {
          ;[datasetname, schemaname, tablename] = (preparedParams.secondary as string).split('.')
        }
        query = dedent`DECLARE
                alias TEXT;
              BEGIN
                ${
                  storedProc
                    ? dedent`
                        CREATE TABLE ${storedProcTable} AS
                        SELECT * FROM ${connection.quoteName(preparedParams.secondary as string)}
                        WHERE 1=0;`
                    : ''
                }
                EXECUTE '
                  with __alias AS(
                    SELECT CONCAT(
                      ''b.'', column_name, '' AS '', column_name, ''_secondary''
                    ) col_alias
                    FROM ${datasetname}.INFORMATION_SCHEMA.COLUMNS
                    WHERE table_name ilike ''${tablename}'' and
                      table_schema ilike ''${schemaname}'' and
                      table_catalog ilike ''${datasetname}''
                  )
                  SELECT STRING_AGG(col_alias, '', '') aliases
                  FROM __alias
                '
                INTO alias;
                ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
                EXECUTE
                  REPLACE(
                    '${dedent(createTable)}',
                    '%s',
                    alias
                  );
              END;`
        break
      }
      case Provider.Redshift: {
        const tempselect = connection.getTempSelectQuery(select, dryRun)
        const temptable = `${outputTablename}_temp`
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempLocation.split('.')
          tablename = `${tempTableName(componentName, preparedParams.secondary)}`
          storedProcTable = connection.quoteName(`${schemaname}.${tablename}`)
        } else {
          ;[datasetname, schemaname, tablename] = (preparedParams.secondary as string).split('.')
        }
        query = dedent`
          DECLARE
            _alias VARCHAR(MAX);
          BEGIN
            ${
              storedProc
                ? dedent`
                    CREATE TABLE ${storedProcTable} AS
                    SELECT * FROM ${connection.quoteName(preparedParams.secondary as string)}
                    WHERE 1=0;`
                : ''
            }
            ${connection.getDropTableQuery(temptable)}
            EXECUTE '
              CREATE TABLE ${temptable} AS
                SELECT DISTINCT
                  ''b.'' || column_name || '' AS '' || column_name || ''_secondary''
                AS col_alias
                FROM svv_columns
                WHERE table_name ILIKE ''${tablename}'' AND
                  table_schema ILIKE ''${schemaname}'' AND
                  table_catalog ILIKE ''${datasetname}''
            ';
            EXECUTE '
              SELECT LISTAGG(col_alias, '', '') aliases
              FROM ${temptable};
            ' INTO _alias;
            ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
            ${connection.getDropTableQuery(outputTablename)}
            EXECUTE
              REPLACE(
                'CREATE TABLE ${outputTablename} AS
                  ${tempselect}',
                '%s',
                _alias
              );
            DROP TABLE ${temptable};
          END;`
        return {
          query,
          outputs: { result: outputTablename }
        }
      }
      case Provider.Snowflake: {
        const tokens = ((preparedParams.secondary as string) ?? '').split('.')
        const datasetname = tokens[0]
        const schemaname = tokens[1]
        const tablename = tokens[2]
        query = dedent`DECLARE
                  alias STRING;
                BEGIN
                  LET res resultset := (EXECUTE IMMEDIATE '
                    with __alias AS(
                      SELECT CONCAT(
                        ''b.'', column_name, '' AS '', column_name, ''_secondary''
                      ) col_alias
                      FROM ${datasetname}.INFORMATION_SCHEMA.COLUMNS
                      WHERE table_name = ''${tablename.toUpperCase()}'' and
                        table_schema = ''${schemaname.toUpperCase()}'' and
                        table_catalog = ''${datasetname.toUpperCase()}''
                    )
                    SELECT LISTAGG(col_alias, '', '') aliases
                    FROM __alias
                  ');
                  LET cur_res CURSOR FOR res;
                  OPEN cur_res;
                  FETCH cur_res INTO alias;
                  EXECUTE IMMEDIATE
                    REPLACE(
                      'BEGIN
                        ${createTable}
                      END;',
                      '%s',
                      alias
                    );
                END;`
        return {
          query,
          outputs: { result: outputTablename }
        }
      }
      case Provider.DatabricksRest: {
        const leftOutputTableName = connection.quoteName(outputTablename + '_left')
        const rightOutputTableName = connection.quoteName(outputTablename + '_right')

        const generateSpatialFilterTablesQuery = (connection as DBricksConnection).generateSpatialFilterTables(
          connection.quoteName(preparedParams.source as string),
          connection.quoteName(preparedParams.secondary as string),
          preparedParams.geosource as string,
          preparedParams.geosecondary as string,
          leftOutputTableName,
          rightOutputTableName
        )

        query = `
from pyspark.sql.functions import col

l_df = spark.sql("select * from ${connection.quoteName(preparedParams.source as string)}")
r_df = spark.sql("select * from ${connection.quoteName(preparedParams.secondary as string)}")

# Get the list of columns from both DataFrames
l_columns = l_df.columns
r_columns = r_df.columns

# Specify the special columns
special_columns = ['__carto_xmin', '__carto_ymin', '__carto_xmax', '__carto_ymax']

# Initialize an empty list to build the SELECT clause
select_clause = []

# Handle left DataFrame columns
for col in l_columns:
    if col in special_columns:
        select_clause.append(f"_main.{col} AS {col}")
    else:
        select_clause.append(f"_main.{col} AS l_{col}")

# Handle right DataFrame columns
for col in r_columns:
    if col in special_columns:
        if col not in l_columns:
            select_clause.append(f"_joined.{col} AS {col}")
    else:
        select_clause.append(f"_joined.{col} AS r_{col}")

# Combine the SELECT statement
select_clause_str = ", ".join(select_clause)

${generateSpatialFilterTablesQuery}
${(connection as DBricksConnection).wrapSQLCodeAsPythonCall(createTable)}`
        break
      }
      case Provider.Oracle: {
        const [, schemaname, tablename] = (preparedParams.secondary as string).split('.')
        query = dedent`DECLARE
                alias CLOB;
              BEGIN
                EXECUTE IMMEDIATE '
                  with __alias AS(
                    SELECT ''b.''||column_name||'' AS ''||column_name||''_secondary'' AS col_alias
                    FROM all_tab_columns
                    WHERE table_name = ''${tablename.toUpperCase()}'' AND owner = ''${schemaname.toUpperCase()}''
                  )
                  SELECT LISTAGG(col_alias, '', '') WITHIN GROUP (ORDER BY col_alias)
                  FROM __alias
                '
                INTO alias;
                EXECUTE IMMEDIATE
                  REPLACE(
                    '${dedent(createTable)}',
                    '%s',
                    alias
                  );
              END;`
        break
      }
      default:
        return null
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params, connection }: GetOutputSchemaParams): TableSchema {
    const schema: TableSchema = []
    schema.push(...columns.source)
    const intersectionColName = `intersection_${params.geosource}_${params.geosecondary}`
    schema.push({
      name: intersectionColName,
      type: connection.getGeoDataType()
    })
    if (connection.type === Provider.DatabricksRest) {
      const mainColumnNames = columns.source.map((column) => column.name)
      for (const column of columns.secondary) {
        if (!mainColumnNames.includes(column.name)) {
          schema.push(column)
        }
      }
      schema.push({
        name: `l___carto_xmin`,
        type: ColumnDataType.Number
      })
      schema.push({
        name: `l___carto_ymin`,
        type: ColumnDataType.Number
      })
      schema.push({
        name: `l___carto_xmax`,
        type: ColumnDataType.Number
      })
      schema.push({
        name: `l___carto_ymax`,
        type: ColumnDataType.Number
      })
      schema.push({
        name: `r___carto_xmin`,
        type: ColumnDataType.Number
      })
      schema.push({
        name: `r___carto_ymin`,
        type: ColumnDataType.Number
      })
      schema.push({
        name: `r___carto_xmax`,
        type: ColumnDataType.Number
      })
      schema.push({
        name: `r___carto_ymax`,
        type: ColumnDataType.Number
      })
    } else {
      columns.secondary.forEach((column) => {
        schema.push({
          name: `${column.name}_secondary`,
          type: column.type
        })
      })
    }
    return schema
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest,
      Provider.Oracle
    ]
  }
}

export { Intersection }
